import { useMemo, useState } from 'react';
import { Button, Input, Popconfirm, Switch, TreeSelect } from 'antd';
import type { TreeSelectProps } from 'antd';
import {
  AlertOutlined, ApiOutlined, CheckOutlined, CloudServerOutlined, DatabaseOutlined,
  DownloadOutlined, InfoCircleOutlined, PauseCircleOutlined, PlayCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type {
  CreateSceneFromCaseFlowState,
  CreateSceneFromJiraState,
  SceneGenMode,
} from '@/apis/simulation';
import { MAX_BATCH_CASES, MAX_BATCH_CASES_LABEL } from '@/constants/sceneBatch';
import styles from './GenerateCards.module.less';
import { Hint, MessageLog, StatusLine, Stepper } from './generateCardParts';
import BatchFailureModal, {
  collectBatchFailures, countCancelledSkips, downloadFailures,
} from './BatchFailureModal';

export type BatchSource = 'jira' | 'caseflow';
export type CaseFlowRegion = 'cn' | 'ae' | 'sea';

/** 与提交时的切分口径一致（index.tsx handleSubmit / handleCreateFromCaseFlow） */
const SPLIT_RE = /[\s,;;，]+/;

interface Props {
  source: BatchSource;
  onSourceChange: (v: BatchSource) => void;
  genMode: SceneGenMode;
  onGenModeChange: (v: SceneGenMode) => void;
  inputValue: string;
  onInputChange: (v: string) => void;
  canManageSecurity: boolean;
  confidential: boolean;
  onConfidentialChange: (v: boolean) => void;
  region: CaseFlowRegion;
  onRegionChange: (v: CaseFlowRegion) => void;
  sceneSetTreeData: TreeSelectProps['treeData'];
  sceneSetNodeId?: number;
  onSceneSetChange: (v: number | undefined) => void;
  state: CreateSceneFromJiraState | CreateSceneFromCaseFlowState | null;
  stateLoading: boolean;
  onRefresh: () => void;
  submitting: boolean;
  onSubmit: () => void;
  /** 取消本批（**不可续跑**）：已发起过就随时可取消，含拉取中断后 */
  onCancelBatch: (jobId: number) => Promise<void>;
  /** 断点续跑：仅 ``interrupted`` 的批次可点 */
  onResumeBatch: (jobId: number) => Promise<void>;
}

interface TileOption<T extends string> {
  value: T;
  name: string;
  desc: string;
  icon: React.ReactNode;
}

const SOURCE_OPTIONS: TileOption<BatchSource>[] = [
  { value: 'caseflow', name: 'Case-Flow', desc: '按 Case ID 拉取', icon: <DatabaseOutlined /> },
  { value: 'jira', name: 'Jira', desc: '按 Issue Key 拉取', icon: <ApiOutlined /> },
];

const MODE_OPTIONS: TileOption<SceneGenMode>[] = [
  { value: 'generate_and_qc_check', name: '事故场景', desc: '生成 + 闭环质检', icon: <AlertOutlined /> },
  { value: 'cloud_cmd', name: '云控场景', desc: 'cloud_cmd 模式', icon: <CloudServerOutlined /> },
];

const REGION_OPTIONS: { value: CaseFlowRegion; label: string }[] = [
  { value: 'cn', label: '国内' },
  { value: 'ae', label: '阿联酋' },
  { value: 'sea', label: '新加坡' },
];

function Tiles<T extends string>({
  options, value, onChange, label,
}: { options: TileOption<T>[]; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className={styles.tiles} role="radiogroup" aria-label={label}>
      {options.map(o => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            className={`${styles.tile} ${on ? styles.tileOn : ''}`}
            onClick={() => onChange(o.value)}
          >
            <span className={styles.tileIcon}>{o.icon}</span>
            <span className={styles.tileText}>
              <div className={styles.tileName}>{o.name}</div>
              <div className={styles.tileDesc}>{o.desc}</div>
            </span>
            <CheckOutlined className={styles.tileCheck} />
          </button>
        );
      })}
    </div>
  );
}

const BatchGenerateCard: React.FC<Props> = props => {
  const {
    source, genMode, inputValue, state, submitting,
  } = props;
  const isJira = source === 'jira';
  const status = state?.status ?? 'idle';
  const running = status === 'running';
  // 中断 = 状态还挂着 running，但后台线程已消失（进程重启等）。账本在 PG 里没丢，
  // 可「继续」；未勾选的 case 不会被丢掉。
  const interrupted = running && !!state?.interrupted;
  const ledgerJobId = state?.jobId ?? null;
  const [failOpen, setFailOpen] = useState(false);
  const [acting, setActing] = useState(false);

  const { count, dupes } = useMemo(() => {
    const all = inputValue.split(SPLIT_RE).map(s => s.trim()).filter(Boolean);
    const uniq = new Set(all);
    return { count: uniq.size, dupes: all.length - uniq.size };
  }, [inputValue]);

  // 后端按「切分后的条数」计数（重复项不折叠），故超限判定也用未去重条数，
  // 保证卡片上的红字与提交时的后端口径一致。
  const submitted = count + dupes;
  const overLimit = submitted > MAX_BATCH_CASES;

  // 进度：tracker 处理阶段（拉取并创建）每 0.5s 落一次 processed/total；
  // total 在任务启动时即写入，首帧就能展示「0 / N」。
  const processed = state?.processedCount ?? null;
  const total = state?.totalCount ?? null;
  const pct = running && processed != null && total
    ? Math.min(100, Math.round((processed / total) * 100))
    : null;
  const runningText = (() => {
    if (!running) return undefined;
    if (interrupted) return '已中断 · 可继续';
    if (state?.stage === 'creating') return '正在创建批次任务…';
    if (state?.stage === 'dispatching') return '正在调度构建…';
    if (processed != null && total != null) return `正在拉取并创建 ${processed} / ${total}`;
    return '正在拉取数据…';
  })();
  // 「拉取 Jira/Case」→「创建场景」是一体链路（逐条拉数据 + 落库），全部拉完才统一下发；
  // 下发阶段后端把 stage 打成 dispatching，才点亮第 2 步「调度构建」。
  const activeStep = state?.stage === 'dispatching' ? 2 : 0;

  // 台账 Job（发起时就建好，立刻可见）；完成后的下发也落同一个 Job，故优先取它。
  const jobId = ledgerJobId ?? state?.result?.generateResult?.jobId ?? undefined;
  const foreign = !isJira && (props.region === 'ae' || props.region === 'sea');
  // 海外走的是 sync 回调创建生成任务的老路径，没有台账 Job，也没有取消 / 续跑。
  const canAct = !!jobId && !foreign;
  // 取消有两副面孔：后端收口（run_job 走到 _settle_cancelled，result.cancelled=true）与
  // 点「取消本批」后 router 直接追平状态（stage=cancelled，没有 result）。两者都算取消，
  // 否则按钮取消过的批次会在步骤条上谎报全绿。
  const cancelled = status === 'completed'
    && (!!state?.result?.cancelled || state?.stage === 'cancelled');

  // 完成态失败明细：逐条原因展示 + 下载 CSV（数据来自 result.results，不再走后端）。
  // 单批上限 5 万条，故走 useMemo —— state 每次轮询都是新对象，别每渲染重算一遍。
  const failures = useMemo(() => collectBatchFailures(state), [state]);
  const skippedByCancel = useMemo(() => countCancelledSkips(state), [state]);

  const meta: string[] = [];
  const reqCount = state && ('keysCount' in state ? state.keysCount : (state as CreateSceneFromCaseFlowState).caseIdsCount);
  if (reqCount) meta.push(`${reqCount} 个${isJira ? ' key' : ' case'}`);
  if (running && processed != null && total != null) {
    // 进度总数已经在 runningText 里，这里只补成功/失败拆分，避免同一行重复。
    const parts: string[] = [];
    if (state?.okCount) parts.push(`${state.okCount} 成功`);
    if (state?.failedCount) parts.push(`${state.failedCount} 失败`);
    if (parts.length) meta.push(parts.join(' · '));
  }
  if (status === 'completed' && state?.result) {
    const r = state.result;
    meta.push(`共 ${r.total ?? 0}`);
    if (r.success) meta.push(`${r.success} 新建`);
    if (r.updated) meta.push(`${r.updated} 已更新`);
    // 取消时后端的 failed 把「没轮到处理」的也算进去了，这里改用明细里的真实失败数，
    // 免得同一行出现两个互相矛盾的数字。
    const failedShown = r.cancelled ? failures.length : (r.failed ?? 0);
    if (failedShown) meta.push(`${failedShown} 失败`);
    if (r.cancelled) {
      meta.push(skippedByCancel ? `已取消，${skippedByCancel} 条未处理` : '已取消');
    }
  } else if (cancelled) {
    // 点按钮取消：router 只回状态不回 result，未处理条数用「最后上报的进度」反推。
    const skipped = Math.max(0, (total ?? 0) - (processed ?? 0));
    meta.push(skipped ? `已取消，${skipped} 条未处理` : '已取消');
  }

  /** 取消 / 继续共用的转圈外壳：错误提示与状态刷新都由父组件负责。 */
  const runAction = async (fn: (id: number) => Promise<void>) => {
    if (!jobId) return;
    setActing(true);
    try {
      await fn(jobId);
    } finally {
      setActing(false);
    }
  };

  return (
    <section className={styles.card} aria-label="场景批量生成">
      <header className={styles.header}>
        <div>
          <div className={styles.title}>
            <span className={styles.titleIcon}><PlayCircleOutlined /></span>
            场景批量生成
          </div>
          <div className={styles.subtitle}>输入 Case ID 或 Jira Key，批量建场景并调度构建</div>
        </div>
        <Stepper
          steps={[isJira ? '拉取 Jira' : '拉取 Case', '创建场景', '调度构建']}
          // 取消 = 停在半路，后两步没走完；借 failed 语义「不点亮已完成」，别谎报全绿。
          status={cancelled ? 'failed' : status}
          activeIndex={activeStep}
        />
      </header>

      <div className={styles.body}>
        <div className={styles.section}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionNo}>01</span>
            <span className={styles.sectionTitle}>来源与类型</span>
          </div>
          <div className={styles.choiceGrid}>
            <div>
              <div className={styles.choiceGroupLabel}>数据来源</div>
              <Tiles label="数据来源" options={SOURCE_OPTIONS} value={source} onChange={props.onSourceChange} />
            </div>
            <div>
              <div className={styles.choiceGroupLabel}>
                场景类型
                <Hint title="下发生成任务的 mode，并记录到场景上；之后在场景管理里重新构建也沿用该模式" />
              </div>
              <Tiles label="场景类型" options={MODE_OPTIONS} value={genMode} onChange={props.onGenModeChange} />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionNo}>02</span>
            <span className={styles.sectionTitle}>{isJira ? 'Jira Issue Key' : 'Case ID'}</span>
            <span className={styles.sectionHint}>
              支持逗号 / 换行 / 空格分隔，单次最多 {MAX_BATCH_CASES_LABEL}个
            </span>
          </div>
          <div className={styles.inputWrap}>
            <Input.TextArea
              aria-label={isJira ? 'Jira Issue Key' : 'Case ID'}
              placeholder={isJira
                ? 'PROJ-1234\nPROJ-1235 PROJ-1236'
                : 'XDT0013_20260702101317\nXDT0014_20260702101318'}
              value={inputValue}
              onChange={e => props.onInputChange(e.target.value)}
              autoSize={{ minRows: 4, maxRows: 8 }}
              variant="borderless"
            />
            <div className={styles.inputMeta}>
              <span className={`${styles.count} ${overLimit ? styles.countOver : ''}`}>
                已识别 <b>{count}</b> 个{dupes > 0 && `（${dupes} 个重复将去重）`}
                {overLimit && `，超过单次上限 ${MAX_BATCH_CASES_LABEL}个`}
              </span>
              {inputValue && (
                <Button size="small" type="text" className={styles.clear} onClick={() => props.onInputChange('')}>
                  清空
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionNo}>03</span>
            <span className={styles.sectionTitle}>生成选项</span>
          </div>
          <div className={styles.optionGrid}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>
                目标场景集 <span className={styles.optional}>（可选）</span>
                <Hint title="场景生成完成、质检合格后自动加入该场景集。只能选择场景集，不能选择目录。" />
              </span>
              <TreeSelect
                style={{ width: '100%' }}
                treeData={props.sceneSetTreeData}
                value={props.sceneSetNodeId}
                onChange={v => props.onSceneSetChange(v as number | undefined)}
                placeholder="不选择则不自动入集"
                allowClear
                showSearch
                treeNodeFilterProp="title"
                popupMatchSelectWidth={false}
              />
            </div>
            {!isJira && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>
                  Case 区域
                  <Hint title="按 case 来源选择；三套 caseflow 数据互不相通，不做跨区兜底" />
                </span>
                <div className={styles.pills} role="radiogroup" aria-label="Case 区域">
                  {REGION_OPTIONS.map(o => (
                    <button
                      key={o.value}
                      type="button"
                      role="radio"
                      aria-checked={props.region === o.value}
                      className={`${styles.pill} ${props.region === o.value ? styles.pillOn : ''}`}
                      onClick={() => props.onRegionChange(o.value)}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {props.canManageSecurity && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>
                  保密场景
                  <Hint title="开启后新建场景打为保密（security_level=1），仅具备 data:confidential:view 的角色可见" />
                </span>
                <Switch
                  size="small"
                  checked={props.confidential}
                  onChange={props.onConfidentialChange}
                  style={{ width: 'fit-content' }}
                />
              </div>
            )}
          </div>
          {foreign && (
            <div className={styles.notice}>
              <InfoCircleOutlined style={{ marginTop: 3 }} />
              <span>
                海外场景分两步：① 数据同步（Azure → 国内 BOS）② 场景生成。
                同步进度见下方「海外数据同步任务」，生成进度见「场景生成任务」。
              </span>
            </div>
          )}
        </div>
      </div>

      <footer className={styles.footer}>
        <StatusLine
          status={status}
          runningText={runningText}
          meta={meta}
          jobId={jobId}
          error={state?.error}
          loading={props.stateLoading}
          onRefresh={props.onRefresh}
        >
          {running && !interrupted && (
            <div className={styles.progress} aria-hidden>
              <div
                className={`${styles.progressBar} ${pct == null ? styles.progressIndeterminate : ''}`}
                style={pct == null ? undefined : { width: `${pct}%` }}
              />
            </div>
          )}
          {interrupted && (
            <div className={styles.interrupt}>
              <ReloadOutlined />
              <span>
                任务已中断（服务重启或执行异常），本批的 Job 与每条记录都已落库，未处理的
                {Math.max(0, (total ?? 0) - (processed ?? 0))} 条可接着跑。
                {state?.error ? `原因：${state.error}` : ''}
              </span>
              <Button
                size="small"
                type="primary"
                loading={acting}
                disabled={!canAct}
                onClick={() => runAction(props.onResumeBatch)}
              >
                继续
              </Button>
            </div>
          )}
          {/* 逐条消息：拉取进度 / 失败原因 / 下发结果，都来自 Redis 里的批次状态 */}
          <MessageLog messages={state?.messages} />
          {status === 'completed' && failures.length > 0 && (
            <div className={styles.failureRow}>
              <Button
                size="small"
                type="link"
                danger
                onClick={() => setFailOpen(true)}
              >
                {failures.length} 个失败，查看原因
              </Button>
              <Button
                size="small"
                type="link"
                icon={<DownloadOutlined />}
                onClick={() => downloadFailures(failures)}
              >
                下载失败明细
              </Button>
            </div>
          )}
        </StatusLine>
        {/* 中断的批次也要能取消：否则「继续」是唯一出路，而同一用户此刻起不了新批次
            （tracker 按用户占位），人就被卡在一条再也跑不完的批次上。 */}
        {running && canAct && (
          <Popconfirm
            title="取消本批？"
            description={interrupted
              ? '本批已中断，取消后剩余条目作废，不可续跑。'
              : '未处理的条目将置为取消，本批不可续跑。'}
            okText="取消本批"
            okButtonProps={{ danger: true }}
            cancelText="再想想"
            onConfirm={() => runAction(props.onCancelBatch)}
          >
            <Button
              className={styles.submit}
              danger
              icon={<PauseCircleOutlined />}
              loading={acting}
            >
              取消本批
            </Button>
          </Popconfirm>
        )}
        <Button
          className={styles.submit}
          type="primary"
          icon={<PlayCircleOutlined />}
          loading={submitting}
          disabled={running}
          onClick={props.onSubmit}
        >
          {interrupted ? '批次未结束' : running ? '生成中…' : count > 0 ? `生成 ${count} 个场景` : '生成场景'}
        </Button>
      </footer>
      <BatchFailureModal
        open={failOpen}
        items={failures}
        onClose={() => setFailOpen(false)}
      />
    </section>
  );
};

export default BatchGenerateCard;
